import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IMissedMatchOffer } from '@nkr/models/index';
import { MatchHistoryType } from '@nkr/enums/index';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: IMissedMatchOffer[],
    isCenterLogin: boolean,
    onGridReady: (e: GridReadyEvent) => void
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();

        e.columnApi.applyColumnState({
            state: [
                {
                    colId: 'recipientCenter',
                    sort: 'asc'
                }
            ]
        });

        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const cols: ColDef[] = [
        {
            colId: 'matchHistoryType',
            field: 'matchHistoryType',
            headerName: 'Type',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            width: 85,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            hide: props.isCenterLogin,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IMissedMatchOffer;

                if (data.matchHistoryType == MatchHistoryType.CrossMatch) {
                    return 'Cross Match';
                }

                return 'Match Offer';
            }
        },

        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Center</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            comparator: Utility.stringSortComparator
        },

        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Alias</SortableHeaderComponent>,
            width: 145,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },

        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            comparator: Utility.stringSortComparator
        },

        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Alias</SortableHeaderComponent>,
            width: 105,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false
        },

        {
            colId: 'offerDateTime',
            field: 'offerDateTime',
            headerName: 'Offer Date/Time',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Offer Date/Time</SortableHeaderComponent>,
            width: 105,
            cellClass: 'list-cell',
            comparator: Utility.dateComparator,
            sortable: true,
            resizable: false
        },


        {
            colId: 'decisionDateTime',
            field: 'decisionDateTime',
            headerName: 'Decision Date/Time',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Decision Date/Time</SortableHeaderComponent>,
            width: 105,
            cellClass: 'list-cell',
            comparator: Utility.dateComparator,
            sortable: true,
            resizable: false
        },

        {
            colId: 'deadlineMissed',
            field: 'deadlineMissed',
            headerName: 'Missed Deadline',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Missed Deadline</SortableHeaderComponent>,
            width: 110,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IMissedMatchOffer;

                return data.deadlineMissed ? 'Yes' : 'No';
            }
        },

        {
            colId: 'decision',
            headerName: 'Decision',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Decision</SortableHeaderComponent>,
            width: 165,
            cellClass: 'list-cell',
            sortable: true,
            resizable: false,
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as IMissedMatchOffer;

                if (data.matchHistoryType == MatchHistoryType.MatchOffer) {
                    if (data.wasAcceptedNoIssues || data.wasAcceptedNotConfident) {
                        return 1;
                    }

                    if (data.wasDeclinedUnacceptable || data.wasDeclinedOther || data.wasDeclinedUnacceptableAbove99Cpra || data.wasAcceptedNotConfidentAbove99Cpra || data.wasDeclinedSurgeonUnavailable) {
                        return 3;
                    }
                } else {
                    return data.vxmAccepted ? 1 : 2;
                }

                return 4;
            },
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMissedMatchOffer;

                if (data.matchHistoryType == MatchHistoryType.MatchOffer) {
                    if (data.wasAcceptedNoIssues || data.wasAcceptedNotConfident) {
                        return <div>Accepted</div>;
                    }

                    if (data.wasDeclinedUnacceptable || data.wasDeclinedOther || data.wasDeclinedUnacceptableAbove99Cpra || data.wasAcceptedNotConfidentAbove99Cpra || data.wasDeclinedSurgeonUnavailable) {
                        return <>Declined</>;
                    }
                } else {
                    return data.vxmAccepted ? <>Accepted</> : <>Not Accepted</>;
                }

                return null;
            }
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={30}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No center information available...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
            >

            </AgGridReact>
        </ReportWrapper>
    );
}

export default Grid;
